import type { Editor } from "@tiptap/core";
import { type Component, type ComponentProps, createEffect, onCleanup, splitProps } from "solid-js";
import { isNil } from "lodash-es";

export type EditorContentProps = {
  editor: Editor | null;
} & ComponentProps<"div">;

export const EditorContent: Component<EditorContentProps> = (props) => {
  const [local, rest] = splitProps(props, ["editor"]);

  let editorContentRef!: HTMLDivElement;
  let mounted = false;

  createEffect(() => {
    if (isNil(local.editor)) {
      return;
    }

    if (!isNil(local.editor.options.element)) {
      if (mounted) {
        return;
      }
      mounted = true;

      if (!isElement(local.editor.options.element)) {
        throw new Error("'element' is not a valid HTML element");
      }

      editorContentRef.append(...local.editor?.options.element.childNodes);
      local.editor.setOptions({
        element: editorContentRef,
      });
      local.editor.createNodeViews();
    }
  });

  onCleanup(() => {
    if (isNil(local.editor)) {
      return;
    }

    if (!isElement(local.editor?.options.element)) {
      throw new Error("'element' is not a valid HTML element");
    }

    if (!local.editor.isDestroyed) {
      local.editor?.view.setProps({
        nodeViews: {},
      });
    }
    if (isNil(local.editor.options.element?.firstChild)) {
      return;
    }

    const newElement = document.createElement("div");
    newElement.append(...local.editor.options.element.childNodes);
    local.editor.setOptions({
      element: newElement,
    });
  });

  return <div ref={editorContentRef} {...rest} />;
};

const isElement = (el: unknown): el is Element => el instanceof Element;
